import { useEffect, useId, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { Button } from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { api } from '@lib/api';
import { AuthLayout } from './AuthLayout';
import { useSignUp, useSignOut } from './hooks/useAuth';
import { useAuthStore } from '@store/auth';

interface DavetBilgisi {
  gecerli: boolean;
  email?: string;
  tenantAdi?: string;
  rolAdi?: string;
  davetEdenAdSoyad?: string | null;
  expiresAt?: string;
}

/** Davet token'ı ile linke ait davet bilgisini (public endpoint) çeker. */
function useDavetBilgisi(token: string | null) {
  return useQuery({
    queryKey: ['auth', 'davet', token],
    enabled: !!token,
    retry: false,
    queryFn: async () =>
      api.get(`auth/davet/${token}`).json<{ data: DavetBilgisi }>(),
  });
}

const schema = z.object({
  firmaAdi: z.string().min(2, 'Firma adı zorunlu'),
  ad: z.string().min(1, 'Ad zorunlu'),
  soyad: z.string().min(1, 'Soyad zorunlu'),
  email: z.string().min(1, 'E-posta zorunlu').email('Geçerli bir e-posta gir'),
  sifre: z
    .string()
    .min(8, 'Şifre en az 8 karakter olmalı')
    .regex(/[A-ZÇĞİÖŞÜ]/, 'En az 1 büyük harf')
    .regex(/[a-zçğıöşü]/, 'En az 1 küçük harf')
    .regex(/[0-9]/, 'En az 1 rakam'),
  telefon: z.string().optional(),
  kvkkAcikRiza: z.literal(true, {
    errorMap: () => ({ message: 'KVKK aydınlatma metnini onaylamanız gerekir' }),
  }),
});
type FormData = z.infer<typeof schema>;

export function RegisterPage() {
  const { t } = useTranslation('auth');
  const nav = useNavigate();
  const signUp = useSignUp();
  const signOut = useSignOut();
  const session = useAuthStore((s) => s.session);
  const me = useAuthStore((s) => s.me);
  const [searchParams] = useSearchParams();
  const davetToken = searchParams.get('davet');
  const davetSorgu = useDavetBilgisi(davetToken);
  const davetBilgisi = davetSorgu.data?.data;
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [signupEmail, setSignupEmail] = useState<string | null>(null);
  const firmaAdiId = useId();
  const adId = useId();
  const soyadId = useId();
  const emailId = useId();
  const sifreId = useId();
  const telefonId = useId();
  const kvkkId = useId();

  // Davet token'ı geldiğinde localStorage'a yaz — useAuth/useMe sync'te bunu okuyup gönderir
  useEffect(() => {
    if (davetToken) {
      localStorage.setItem('davetToken', davetToken);
    }
  }, [davetToken]);

  // Davet token + session yoksa normal register; varsa kullanıcıya soracağız.
  // Davet yok + session varsa dashboard'a yönlendir.
  useEffect(() => {
    if (!davetToken && session) {
      nav('/dashboard');
    }
  }, [davetToken, session, nav]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  // Davet e-postası doğrulanınca formdaki e-posta alanını ön-doldur (kilitli).
  useEffect(() => {
    if (davetBilgisi?.gecerli && davetBilgisi.email) {
      setValue('email', davetBilgisi.email, { shouldValidate: true });
    }
  }, [davetBilgisi, setValue]);

  const davetEmailKilitli = !!davetToken && davetBilgisi?.gecerli === true;

  const onSubmit = handleSubmit(async (data) => {
    setSubmitError(null);
    try {
      const result = await signUp.mutateAsync(data);
      if (result.session) {
        // Email confirmation kapalıysa direkt dashboard
        nav('/dashboard');
      } else {
        // Email doğrulama bekleniyor
        setSignupEmail(data.email);
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Kayıt başarısız');
    }
  });

  // Davet token var + kullanıcı login → karar kartı
  if (davetToken && session) {
    const mevcutMail = me?.email ?? session.user?.email ?? '(bilinmeyen)';
    return (
      <AuthLayout title="Davet linkini açtınız">
        <p className="text-sm text-muted-foreground">
          Şu an <strong>{mevcutMail}</strong> hesabıyla giriş yapmış durumdasınız.
          Bu davet farklı bir kullanıcı için olabilir. Ne yapmak istersiniz?
        </p>
        <div className="mt-6 flex flex-col gap-3">
          <Button
            type="button"
            className="w-full"
            disabled={signOut.isPending}
            onClick={() => signOut.mutate()}
          >
            {signOut.isPending ? 'Çıkış yapılıyor…' : 'Çıkış yap ve daveti kabul et'}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => {
              localStorage.removeItem('davetToken');
              nav('/dashboard');
            }}
          >
            Mevcut hesabımla devam et
          </Button>
        </div>
      </AuthLayout>
    );
  }

  // Email doğrulama bekleme ekranı
  if (signupEmail) {
    return (
      <AuthLayout title={t('register.basarili_baslik')}>
        <p className="text-sm text-muted-foreground">
          {t('register.basarili_aciklama', { email: signupEmail })}
        </p>
        <div className="mt-6 rounded-md border border-primary/30 bg-primary/5 p-4 text-sm">
          📧 <strong>{signupEmail}</strong> adresine gönderdiğimiz linke tıklayınca
          hesabın aktive olacak ve panele yönlendirileceksin.
        </div>
        <Link
          to="/auth/login"
          className="mt-6 inline-block text-sm text-primary hover:underline"
        >
          ← Giriş ekranına dön
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('register.baslik')} subtitle={t('register.alt_baslik')}>
      {davetToken && davetSorgu.isLoading && (
        <div className="mb-4 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          Davet bilgisi yükleniyor…
        </div>
      )}
      {davetToken && !davetSorgu.isLoading && davetBilgisi?.gecerli && (
        <div className="mb-4 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
          🎉 <strong>{davetBilgisi.davetEdenAdSoyad || 'Bir yönetici'}</strong> sizi{' '}
          <strong>{davetBilgisi.tenantAdi}</strong> ekibine{' '}
          <strong>{davetBilgisi.rolAdi}</strong> rolüyle davet etti — kayıt olduğunda
          otomatik olarak bu firmaya katılacaksın.
          <br />
          <span className="text-xs text-muted-foreground">
            Firma adı alanı bu durumda kullanılmaz, dilediğin bir değer yazabilirsin.
          </span>
        </div>
      )}
      {davetToken && !davetSorgu.isLoading && davetBilgisi && !davetBilgisi.gecerli && (
        <div className="mb-4 rounded-md border border-danger/30 bg-danger/5 px-3 py-2 text-sm text-danger">
          Bu davet linki geçersiz veya süresi dolmuş. Normal kayıt yapabilir ya da
          daveti gönderen kişiden yeni bir davet isteyebilirsin.
        </div>
      )}
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <div>
          <label htmlFor={firmaAdiId} className="block text-sm font-medium mb-1.5">
            {t('register.firma_adi')}
          </label>
          <Input
            id={firmaAdiId}
            placeholder={t('register.firma_adi_placeholder')}
            {...register('firmaAdi')}
            aria-invalid={!!errors.firmaAdi}
          />
          {errors.firmaAdi && (
            <p className="mt-1 text-xs text-danger">{errors.firmaAdi.message}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor={adId} className="block text-sm font-medium mb-1.5">
              {t('register.ad')}
            </label>
            <Input
              id={adId}
              autoComplete="given-name"
              {...register('ad')}
              aria-invalid={!!errors.ad}
            />
            {errors.ad && (
              <p className="mt-1 text-xs text-danger">{errors.ad.message}</p>
            )}
          </div>
          <div>
            <label htmlFor={soyadId} className="block text-sm font-medium mb-1.5">
              {t('register.soyad')}
            </label>
            <Input
              id={soyadId}
              autoComplete="family-name"
              {...register('soyad')}
              aria-invalid={!!errors.soyad}
            />
            {errors.soyad && (
              <p className="mt-1 text-xs text-danger">{errors.soyad.message}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor={emailId} className="block text-sm font-medium mb-1.5">
            {t('register.email')}
          </label>
          <Input
            id={emailId}
            type="email"
            autoComplete="email"
            {...register('email')}
            aria-invalid={!!errors.email}
            readOnly={davetEmailKilitli}
            className={davetEmailKilitli ? 'bg-muted/40 cursor-not-allowed' : undefined}
          />
          {davetEmailKilitli && (
            <p className="mt-1 text-xs text-muted-foreground">
              Bu e-posta davet linkine bağlı, değiştirilemez.
            </p>
          )}
          {errors.email && (
            <p className="mt-1 text-xs text-danger">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label htmlFor={sifreId} className="block text-sm font-medium mb-1.5">
            {t('register.sifre')}
          </label>
          <Input
            id={sifreId}
            type="password"
            autoComplete="new-password"
            {...register('sifre')}
            aria-invalid={!!errors.sifre}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            {t('register.sifre_yardim')}
          </p>
          {errors.sifre && (
            <p className="mt-1 text-xs text-danger">{errors.sifre.message}</p>
          )}
        </div>

        <div>
          <label htmlFor={telefonId} className="block text-sm font-medium mb-1.5">
            {t('register.telefon')}
          </label>
          <Input
            id={telefonId}
            type="tel"
            autoComplete="tel"
            placeholder="0(555) 111 22 33"
            {...register('telefon')}
          />
        </div>

        <label htmlFor={kvkkId} className="inline-flex w-fit items-start gap-3 cursor-pointer">
          <input
            id={kvkkId}
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-input text-primary focus:ring-ring"
            {...register('kvkkAcikRiza')}
          />
          <span className="text-sm">
            <a
              href="/kvkk"
              target="_blank"
              rel="noreferrer"
              className="text-primary hover:underline"
            >
              KVKK Aydınlatma Metni
            </a>
            'ni okudum ve onaylıyorum.
          </span>
        </label>
        {errors.kvkkAcikRiza && (
          <p className="text-xs text-danger">{errors.kvkkAcikRiza.message}</p>
        )}

        {submitError && (
          <div className="rounded-md border border-danger/30 bg-danger/5 px-3 py-2 text-sm text-danger">
            {submitError}
          </div>
        )}

        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? t('register.olusturuluyor') : t('register.kayit_ol')}
        </Button>

        <p className="text-center text-sm text-muted-foreground">
          {t('register.hesap_var')}{' '}
          <Link to="/auth/login" className="text-primary hover:underline">
            {t('register.giris_yap')}
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
